import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTagsUpdate } from "../../hooks/useTags";
import { FlatVsaaTag, UpdateTagRequest } from "../../models/VsaaTag";
import {
  getParentIdFromAncestors,
  hasDuplicateNameAtSameLevel,
} from "../../services/TagService";
import { ButtonWithLoading } from "../ButtonWithLoading";

interface EditTreeItemDialogProps {
  open: boolean;
  tagCollectionId: string;
  id: string | undefined;
  title: string;
  onClose: () => void;
  onSave: () => void;
  ancestorIds: string[] | undefined;
  inputValue: string | undefined;
  allTags: FlatVsaaTag[];
}

export const EditTreeItemDialog = (props: EditTreeItemDialogProps) => {
  const {
    open,
    id,
    tagCollectionId,
    title,
    onClose,
    onSave,
    ancestorIds,
    inputValue,
    allTags,
  } = props;
  const [updatedTagName, setUpdatedTagName] = useState<string>(
    inputValue || ""
  );
  const [duplicateError, setDuplicateError] = useState<boolean>(false);
  const { mutateAsync: updateTag, isPending: updateTagPending } =
    useTagsUpdate(tagCollectionId);
  const { t } = useTranslation();

  const onTagNameInputChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value;
    setUpdatedTagName(value);

    // Check for duplicates
    if (value.trim()) {
      const parentId = getParentIdFromAncestors(ancestorIds);
      const isDuplicate = hasDuplicateNameAtSameLevel(
        value,
        parentId,
        allTags,
        id
      );
      setDuplicateError(isDuplicate);
    } else {
      setDuplicateError(false);
    }
  };

  const onClickUpdate = async () => {
    if (!updatedTagName || duplicateError) {
      return;
    }

    const request: UpdateTagRequest = {
      id: id,
      title: updatedTagName,
      ancestorIds: ancestorIds,
    };

    await updateTag(request);
    onSave();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !updateTagPending) {
      onClickUpdate();
    }
  };

  return (
    <Dialog open={open}>
      <DialogSurface aria-describedby={undefined} style={{ minWidth: "650px" }}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {duplicateError && (
              <MessageBar intent="info">
                <MessageBarBody>{t("duplicateTagNameError")}</MessageBarBody>
              </MessageBar>
            )}
            <Input
              value={updatedTagName}
              onChange={onTagNameInputChange}
              onKeyDown={handleKeyDown}
            />
          </DialogContent>
          <DialogActions>
            <ButtonWithLoading
              text={t("update")}
              disabled={updateTagPending || duplicateError}
              onClick={onClickUpdate}
              isLoading={updateTagPending}
            />
            <Button
              appearance="secondary"
              onClick={onClose}
              disabled={updateTagPending}
            >
              {t("close")}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
